/**
 * Accessible Menu Hover Behavior
 * - Desktop: Open dropdown on hover (title or arrow)
 * - Mobile: Open dropdown on click (arrow only) - native behavior
 * - Title always navigates on click
 */

class AccessibleMenuHover {
  constructor() {
    this.isDesktop = window.matchMedia('(min-width: 992px)').matches;
    this.menuItems = new Map(); // Track items and their handlers
    this.init();

    // Re-initialize on resize
    window.addEventListener('resize', this.debounce(() => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.matchMedia('(min-width: 992px)').matches;

      // Only re-init if we switched between mobile/desktop
      if (wasDesktop !== this.isDesktop) {
        this.cleanup();
        this.init();
      }
    }, 250));
  }

  cleanup() {
    // Remove all event listeners
    this.menuItems.forEach((handlers, item) => {
      const hoverTarget = handlers.hoverTarget || item;
      if (handlers.mouseenter) {
        hoverTarget.removeEventListener('mouseenter', handlers.mouseenter);
        // Also remove from dropdown if it exists
        if (handlers.dropdown) {
          handlers.dropdown.removeEventListener('mouseenter', handlers.mouseenter);
        }
      }
      if (handlers.mouseleave) {
        hoverTarget.removeEventListener('mouseleave', handlers.mouseleave);
        // Also remove from dropdown if it exists
        if (handlers.dropdown) {
          handlers.dropdown.removeEventListener('mouseleave', handlers.mouseleave);
        }
      }
      if (handlers.syncExpanded) {
        const details = item.querySelector('details');
        if (details) details.removeEventListener('toggle', handlers.syncExpanded);
      }
      if (handlers.summaryClick) {
        const summary = item.querySelector('summary');
        if (summary) {
          summary.removeEventListener('click', handlers.summaryClick);
          if (handlers.summaryKeydown) {
            summary.removeEventListener('keydown', handlers.summaryKeydown);
          }
        }
      }
    });
    this.menuItems.clear();
  }

  init() {
    // Skip initialization entirely on mobile - let native behavior work
    if (!this.isDesktop) {
      return;
    }

    const menuItems = document.querySelectorAll('.menu-item-with-dropdown');

    menuItems.forEach(item => {
      const details = item.querySelector('details');
      const anchor = item.querySelector('a');

      if (!details || !anchor) return;

      this.setupDesktopMenuItem(item);
    });
  }

  setupDesktopMenuItem(item) {
    const details = item.querySelector('details');
    const summary = item.querySelector('summary');
    // The parent link now sits beside the details element instead of inside the
    // summary, so the link and the disclosure control are no longer nested. Older
    // markup that still nests the link is supported as a fallback.
    const anchor = item.querySelector(':scope > a.header__menu-item') || (summary ? summary.querySelector('a') : null);

    if (!details || !summary || !anchor) return;

    // When the link is outside the summary, listen on the whole item so that hovering
    // the link text still opens the dropdown, exactly as hovering the summary did.
    const hoverTarget = summary.contains(anchor) ? summary : item;

    let timeoutId = null;

    // Prevent anchor click from toggling details
    anchor.addEventListener('click', (e) => {
      e.stopPropagation();
      // Anchor navigates normally
    });

    // Create handlers
    const handlers = {
      mouseenter: () => {
        // Clear any pending close timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        details.setAttribute('open', '');
      },
      mouseleave: () => {
        // Delay closing to allow mouse to move to dropdown
        timeoutId = setTimeout(() => {
          details.removeAttribute('open');
          timeoutId = null;
        }, 150);
      },
      summaryClick: (e) => {
        // If click was on anchor, let it navigate
        if (e.target.closest('a')) {
          return;
        }
        // Allow keyboard activation (Enter/Space) to toggle native details behavior
        if (e.detail === 0) {
          return;
        }
        // Otherwise prevent toggle
        e.preventDefault();
      },
      summaryKeydown: (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.target.closest('a')) return;

        e.preventDefault();
        const isOpen = details.hasAttribute('open');

        if (isOpen) {
          details.removeAttribute('open');
        } else {
          details.setAttribute('open', '');
        }
      }
    };

    // Hover on the item (or on the summary, for nested markup) opens the dropdown
    hoverTarget.addEventListener('mouseenter', handlers.mouseenter);
    hoverTarget.addEventListener('mouseleave', handlers.mouseleave);

    // Also add listeners to the dropdown content itself
    const dropdown = details.querySelector('.header__submenu, [id^="HeaderMenu-MenuList"], [id^="link-"]');
    if (dropdown) {
      dropdown.addEventListener('mouseenter', handlers.mouseenter);
      dropdown.addEventListener('mouseleave', handlers.mouseleave);
    }

    summary.addEventListener('click', handlers.summaryClick);
    summary.addEventListener('keydown', handlers.summaryKeydown);

    // Keep aria-expanded in step with the details element. Hover, the Enter and Space
    // handler above, and the disclosure component all change the open attribute
    // directly, and none of them updated aria-expanded, so assistive technology heard
    // "collapsed" on a menu that was open. The toggle event covers every one of them.
    handlers.syncExpanded = () => {
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
    };
    details.addEventListener('toggle', handlers.syncExpanded);
    handlers.syncExpanded();

    // Store handlers and dropdown reference for cleanup
    this.menuItems.set(item, { ...handlers, dropdown, hoverTarget });
  }  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccessibleMenuHover();
  });
} else {
  new AccessibleMenuHover();
}

// Re-initialize on Shopify section reload
document.addEventListener('shopify:section:load', () => {
  new AccessibleMenuHover();
});
